import React, { useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import {
	ArrowLeft,
	Building2,
	Globe,
	Mail,
	MapPin,
	Phone,
	Power,
	Save,
	UserPlus,
	Users,
} from 'lucide-react';

import {
	useBulkUpdateUserRolesMutation,
	useGetEnterpriseQuery,
	useInviteUserToEnterpriseMutation,
	useUpdateAdminUserMutation,
	useUpdateEnterpriseStatusMutation,
} from '../api/admin';
import APIKeysTab from '../components/admin/APIKeysTab';
import {
	Alert,
	Badge,
	Button,
	Card,
	CardBody,
	CardHeader,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	PageContent,
	PageHeader,
	SelectInput,
	SkeletonTable,
	StatusBadge,
	Typography,
} from '../components/common';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../providers/AppAuthProvider';
import { hasAnyRole, hasRole } from '../utils/utils';

const EnterpriseDetailPage: React.FC = () => {
	const { enterpriseId } = useParams<{ enterpriseId: string }>();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { showToast } = useToast();

	// Check if user can view enterprise details (tenant_admin or super_admin)
	const canViewEnterpriseDetails = React.useMemo(() => {
		return hasAnyRole(user?.roles, ['super_admin', 'tenant_admin']);
	}, [user?.roles]);

	// Check if user is super_admin
	const isSuperAdmin = React.useMemo(() => {
		return hasRole(user?.roles, 'super_admin');
	}, [user?.roles]);

	// Check if user has required role (tenant_admin or super_admin only)
	React.useEffect(() => {
		if (user?.roles && !canViewEnterpriseDetails) {
			showToast(
				'Access Denied',
				'Only tenant administrators and super administrators can view organization details',
				'error',
			);
			navigate('/admin');
		}
	}, [user?.roles, canViewEnterpriseDetails, navigate, showToast]);

	const [showStatusModal, setShowStatusModal] = useState(false);
	const [pendingStatusChange, setPendingStatusChange] = useState<
		boolean | null
	>(null);
	const [userRoleChanges, setUserRoleChanges] = useState<
		Record<string, string>
	>({});
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [showInviteModal, setShowInviteModal] = useState(false);
	const [inviteEmail, setInviteEmail] = useState('');
	const [inviteName, setInviteName] = useState('');
	const [inviteRole, setInviteRole] = useState('user');

	const {
		data: enterpriseData,
		isLoading,
		refetch,
	} = useGetEnterpriseQuery(enterpriseId || '', {
		skip: !enterpriseId,
	});

	const [updateEnterpriseStatus, { isLoading: updatingStatus }] =
		useUpdateEnterpriseStatusMutation();
	const [bulkUpdateUserRoles, { isLoading: updatingRole }] =
		useBulkUpdateUserRolesMutation();
	const [updateAdminUser, { isLoading: updatingUser }] =
		useUpdateAdminUserMutation();
	const [inviteUser, { isLoading: invitingUser }] =
		useInviteUserToEnterpriseMutation();

	const enterprise = enterpriseData?.enterprise;

	// Initialize user role changes from enterprise data
	React.useEffect(() => {
		if (enterprise?.users) {
			const initialRoles: Record<string, string> = {};
			enterprise.users.forEach((user) => {
				// Get the primary role (first role or 'user' as default)
				initialRoles[user.id] = user.roles[0] || 'user';
			});
			setUserRoleChanges(initialRoles);
			// Reset unsaved changes when enterprise data loads/changes
			setHasUnsavedChanges(false);
		}
	}, [enterprise]);

	// Check if there are actual changes by comparing with original roles
	React.useEffect(() => {
		if (!enterprise?.users) {
			setHasUnsavedChanges(false);
			return;
		}

		const hasChanges = enterprise.users.some((user) => {
			const currentRole = userRoleChanges[user.id] || user.roles[0] || 'user';
			const originalRole = user.roles[0] || 'user';
			return currentRole !== originalRole;
		});

		setHasUnsavedChanges(hasChanges);
	}, [userRoleChanges, enterprise]);

	const handleRoleChange = (userId: string, newRole: string) => {
		setUserRoleChanges((prev) => ({
			...prev,
			[userId]: newRole,
		}));
		// The useEffect will automatically check for changes and update hasUnsavedChanges
	};

	const handleSaveRoleChanges = async () => {
		if (!enterprise) return;

		try {
			const updates = Object.entries(userRoleChanges)
				.map(([userId, newRole]) => {
					const user = enterprise.users.find((u) => u.id === userId);
					if (user && user.roles[0] !== newRole) {
						return { user_id: userId, role: newRole };
					}
					return null;
				})
				.filter(
					(update): update is { user_id: string; role: string } =>
						update !== null,
				);

			if (updates.length === 0) {
				showToast('Info', 'No role changes to save', 'info');
				return;
			}

			// Bulk update all changed roles
			const result = await bulkUpdateUserRoles({ updates }).unwrap();

			if (result.failed && result.failed.length > 0) {
				const failedCount = result.failed.length;
				const successCount = result.updated.length;
				showToast(
					'Warning',
					`Updated ${successCount} user(s), ${failedCount} failed. Check console for details.`,
					'warning',
				);
				console.error('Failed updates:', result.failed);
			} else {
				showToast(
					'Success',
					result.message || 'User roles updated successfully',
					'success',
				);
			}

			setHasUnsavedChanges(false);
			refetch();
		} catch (error: any) {
			console.error('Error updating user roles:', error);
			const errorMessage =
				error?.data?.message || error?.message || 'Failed to update user roles';
			showToast('Error', errorMessage, 'error');
		}
	};

	const handleStatusChange = async () => {
		if (!enterprise || pendingStatusChange === null) return;

		try {
			await updateEnterpriseStatus({
				enterpriseId: enterprise.enterprise_id,
				data: { active: pendingStatusChange },
			}).unwrap();

			showToast(
				'Success',
				`Organization ${pendingStatusChange ? 'activated' : 'deactivated'} successfully`,
				'success',
			);
			setShowStatusModal(false);
			setPendingStatusChange(null);
			refetch();
		} catch (error: any) {
			console.error('Error updating enterprise status:', error);
			const errorMessage =
				error?.data?.message ||
				error?.message ||
				'Failed to update organization status';
			showToast('Error', errorMessage, 'error');
		}
	};

	const formatDate = (dateString?: string | null) => {
		if (!dateString) return 'N/A';
		try {
			return new Date(dateString).toLocaleString();
		} catch {
			return dateString;
		}
	};

	const getRoleBadge = (role: string) => {
		const variants: Record<
			string,
			'danger' | 'warning' | 'info' | 'secondary'
		> = {
			super_admin: 'danger',
			tenant_admin: 'danger',
			admin: 'warning',
			enterprise_admin: 'warning',
			enterprise_user: 'info',
			tenant_analyst: 'info',
			enterprise_analyst: 'info',
			user: 'secondary',
		};
		return (
			<Badge variant={variants[role] || 'secondary'}>
				{role.replace('_', ' ').toUpperCase()}
			</Badge>
		);
	};

	if (isLoading) {
		return (
			<div className="flex flex-1 flex-col">
				<PageHeader title="Organization Details" variant="title" />
				<PageContent className="max-w-[1280px] m-auto">
					<SkeletonTable rows={10} columns={5} showHeader={true} />
				</PageContent>
			</div>
		);
	}

	if (!enterprise) {
		return (
			<div className="flex flex-1 flex-col">
				<PageHeader title="Organization Details" variant="title" />
				<PageContent className="max-w-[1280px] m-auto">
					<Alert variant="error">Organization not found</Alert>
				</PageContent>
			</div>
		);
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader
				title="Organization Details"
				variant="title"
				actions={
					<div className="flex items-center gap-2">
						<Button
							onClick={() => navigate('/admin')}
							variant="outline"
							icon={ArrowLeft}
						>
							Back
						</Button>
						<Button
							onClick={() => {
								setPendingStatusChange(!enterprise.active);
								setShowStatusModal(true);
							}}
							variant={enterprise.active ? 'danger' : 'success'}
							icon={Power}
						>
							{enterprise.active ? 'Deactivate' : 'Activate'}
						</Button>
					</div>
				}
			/>

			<PageContent className="max-w-[1280px] m-auto">
				{/* Enterprise Information */}
				<Card className="mb-6">
					<CardHeader>
						<div className="flex items-center justify-between">
							<Typography variant="h4">Organization Information</Typography>
							<StatusBadge status={enterprise.active ? 'active' : 'inactive'}>
								{enterprise.active ? 'Active' : 'Inactive'}
							</StatusBadge>
						</div>
					</CardHeader>
					<CardBody>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div>
								<Typography variant="body2" color="muted" className="mb-1">
									Company Name
								</Typography>
								<div className="flex items-center gap-2">
									<Building2 className="w-4 h-4 text-gray-400" />
									<Typography variant="body1" weight="medium">
										{enterprise.company_name}
									</Typography>
								</div>
							</div>

							<div>
								<Typography variant="body2" color="muted" className="mb-1">
									Organization ID
								</Typography>
								<Typography
									variant="body1"
									weight="medium"
									className="font-mono text-sm"
								>
									{enterprise.enterprise_id}
								</Typography>
							</div>

							{enterprise.code && (
								<div>
									<Typography variant="body2" color="muted" className="mb-1">
										Code
									</Typography>
									<Typography variant="body1" weight="medium">
										{enterprise.code}
									</Typography>
								</div>
							)}

							{enterprise.region && (
								<div>
									<Typography variant="body2" color="muted" className="mb-1">
										Region
									</Typography>
									<Typography variant="body1" weight="medium">
										{enterprise.region}
									</Typography>
								</div>
							)}

							{enterprise.contact?.email && (
								<div>
									<Typography variant="body2" color="muted" className="mb-1">
										Contact Email
									</Typography>
									<div className="flex items-center gap-2">
										<Mail className="w-4 h-4 text-gray-400" />
										<Typography variant="body1" weight="medium">
											{enterprise.contact.email}
										</Typography>
									</div>
								</div>
							)}

							{enterprise.contact?.phone && (
								<div>
									<Typography variant="body2" color="muted" className="mb-1">
										Contact Phone
									</Typography>
									<div className="flex items-center gap-2">
										<Phone className="w-4 h-4 text-gray-400" />
										<Typography variant="body1" weight="medium">
											{enterprise.contact.phone}
										</Typography>
									</div>
								</div>
							)}

							{enterprise.contact?.website && (
								<div>
									<Typography variant="body2" color="muted" className="mb-1">
										Website
									</Typography>
									<div className="flex items-center gap-2">
										<Globe className="w-4 h-4 text-gray-400" />
										<Typography variant="body1" weight="medium">
											{enterprise.contact.website}
										</Typography>
									</div>
								</div>
							)}

							{enterprise.address && (
								<div className="md:col-span-2">
									<Typography variant="body2" color="muted" className="mb-1">
										Address
									</Typography>
									<div className="flex items-start gap-2">
										<MapPin className="w-4 h-4 text-gray-400 mt-1" />
										<Typography variant="body1" weight="medium">
											{[
												enterprise.address.street,
												enterprise.address.city,
												enterprise.address.state,
												enterprise.address.zip,
												enterprise.address.country,
											]
												.filter(Boolean)
												.join(', ')}
										</Typography>
									</div>
								</div>
							)}
						</div>
					</CardBody>
				</Card>

				{/* Users Section */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<Typography variant="h4">
								Users ({enterprise.users.length})
							</Typography>
							<div className="flex items-center gap-2">
								{hasUnsavedChanges && (
									<Button
										onClick={handleSaveRoleChanges}
										variant="primary"
										loading={updatingRole}
										icon={Save}
									>
										Save Role Changes
									</Button>
								)}
								<Button
									onClick={() => setShowInviteModal(true)}
									variant="primary"
									icon={UserPlus}
								>
									Invite User
								</Button>
							</div>
						</div>
					</CardHeader>
					<CardBody>
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Name
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Email
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Current Role
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Change Role
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Status
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Last Login
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actions
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{enterprise.users.map((enterpriseUser) => {
										const isActive = enterpriseUser.status === 'active';
										const isCurrentUser =
											user && String(enterpriseUser.id) === String(user.id);

										return (
											<tr key={enterpriseUser.id} className="hover:bg-gray-50">
												<td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
													<div className="flex items-center gap-2">
														<Users className="w-4 h-4 text-gray-400" />
														{enterpriseUser.name}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{enterpriseUser.email}
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													{enterpriseUser.roles.length > 0 ? (
														enterpriseUser.roles.map((role) => (
															<span key={role} className="mr-2">
																{getRoleBadge(role)}
															</span>
														))
													) : (
														<Badge variant="secondary">No Role</Badge>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													{isCurrentUser ? (
														<span className="text-sm text-gray-500 italic">
															Current user
														</span>
													) : (
														<div className="w-40">
															<SelectInput
																value={
																	userRoleChanges[enterpriseUser.id] ||
																	enterpriseUser.roles[0] ||
																	'user'
																}
																onChange={(value) =>
																	handleRoleChange(enterpriseUser.id, value)
																}
																options={[
																	{ value: 'user', label: 'User' },
																	{ value: 'admin', label: 'Admin' },
																	{
																		value: 'enterprise_user',
																		label: 'Organization User',
																	},
																	{
																		value: 'enterprise_admin',
																		label: 'Organization Admin',
																	},
																	...(canViewEnterpriseDetails
																		? [
																				{
																					value: 'tenant_admin',
																					label: 'Tenant Admin',
																				},
																				{
																					value: 'tenant_analyst',
																					label: 'Tenant Analyst',
																				},
																				{
																					value: 'enterprise_analyst',
																					label: 'Organization Analyst',
																				},
																			]
																		: []),
																	...(isSuperAdmin
																		? [
																				{
																					value: 'super_admin',
																					label: 'Super Admin',
																				},
																			]
																		: []),
																]}
																fullWidth={true}
															/>
														</div>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<StatusBadge
														status={
															enterpriseUser.status === 'active'
																? 'active'
																: enterpriseUser.status ===
																	  'pending_verification'
																	? 'pending'
																	: enterpriseUser.status === 'failed' ||
																		  enterpriseUser.status === 'inactive'
																		? 'inactive'
																		: 'pending'
														}
													>
														{enterpriseUser.status}
													</StatusBadge>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
													{formatDate(enterpriseUser.last_login_at)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													{!isCurrentUser && (
														<div className="w-32">
															<Button
																onClick={async () => {
																	try {
																		await updateAdminUser({
																			userId: enterpriseUser.id,
																			data: { active: !isActive },
																		}).unwrap();
																		showToast(
																			'Success',
																			`User ${!isActive ? 'activated' : 'deactivated'} successfully`,
																			'success',
																		);
																		refetch();
																	} catch (error: any) {
																		const errorMessage =
																			error?.data?.message ||
																			error?.message ||
																			`Failed to ${!isActive ? 'activate' : 'deactivate'} user`;
																		showToast('Error', errorMessage, 'error');
																	}
																}}
																variant={isActive ? 'danger' : 'success'}
																size="sm"
																icon={Power}
																loading={updatingUser}
																fullWidth={true}
															>
																{isActive ? 'Deactivate' : 'Activate'}
															</Button>
														</div>
													)}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>

							{enterprise.users.length === 0 && (
								<div className="text-center py-8">
									<p className="text-gray-500">
										No users found in this organization.
									</p>
								</div>
							)}
						</div>
					</CardBody>
				</Card>

				{/* API Keys Section */}
				<div className="mt-6">
					<APIKeysTab enterpriseId={enterprise.enterprise_id} />
				</div>

				{/* Status Change Confirmation Modal */}
				<Modal
					isOpen={showStatusModal}
					onClose={() => {
						setShowStatusModal(false);
						setPendingStatusChange(null);
					}}
					title={
						pendingStatusChange
							? 'Activate Organization'
							: 'Deactivate Organization'
					}
					size="md"
				>
					<ModalBody>
						<Alert
							variant={pendingStatusChange ? 'success' : 'error'}
							className="mb-4"
						>
							{pendingStatusChange
								? 'Activating this organization will allow all users to log in.'
								: 'Deactivating this organization will prevent all users from logging in.'}
						</Alert>
						<Typography variant="body2" color="muted">
							Are you sure you want to{' '}
							{pendingStatusChange ? 'activate' : 'deactivate'}{' '}
							<strong>{enterprise.company_name}</strong>?
						</Typography>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowStatusModal(false);
								setPendingStatusChange(null);
							}}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={handleStatusChange}
							disabled={updatingStatus}
							variant={pendingStatusChange ? 'success' : 'danger'}
							loading={updatingStatus}
						>
							{pendingStatusChange ? 'Activate' : 'Deactivate'}
						</Button>
					</ModalFooter>
				</Modal>

				{/* Invite User Modal */}
				<Modal
					isOpen={showInviteModal}
					onClose={() => {
						setShowInviteModal(false);
						setInviteEmail('');
						setInviteName('');
						setInviteRole('user');
					}}
					title="Invite User to Organization"
					size="md"
				>
					<ModalBody>
						<div className="space-y-4">
							<div>
								<Typography variant="body2" className="mb-1">
									Email Address *
								</Typography>
								<Input
									type="email"
									value={inviteEmail}
									onChange={(e) => setInviteEmail(e.target.value)}
									placeholder="user@example.com"
									fullWidth
								/>
							</div>
							<div>
								<Typography variant="body2" className="mb-1">
									Name (Optional)
								</Typography>
								<Input
									type="text"
									value={inviteName}
									onChange={(e) => setInviteName(e.target.value)}
									placeholder="User Name"
									fullWidth
								/>
							</div>
							<div>
								<Typography variant="body2" className="mb-1">
									Role *
								</Typography>
								<SelectInput
									value={inviteRole}
									onChange={(value) => setInviteRole(value)}
									options={[
										{ value: 'user', label: 'User' },
										{ value: 'admin', label: 'Admin' },
										{ value: 'enterprise_user', label: 'Organization User' },
										{ value: 'enterprise_admin', label: 'Organization Admin' },
										...(canViewEnterpriseDetails
											? [
													{ value: 'tenant_admin', label: 'Tenant Admin' },
													{ value: 'tenant_analyst', label: 'Tenant Analyst' },
													{
														value: 'enterprise_analyst',
														label: 'Organization Analyst',
													},
												]
											: []),
										...(isSuperAdmin
											? [{ value: 'super_admin', label: 'Super Admin' }]
											: []),
									]}
									fullWidth
								/>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button
							onClick={() => {
								setShowInviteModal(false);
								setInviteEmail('');
								setInviteName('');
								setInviteRole('user');
							}}
							variant="secondary"
						>
							Cancel
						</Button>
						<Button
							onClick={async () => {
								if (!inviteEmail || !enterprise) return;
								try {
									await inviteUser({
										enterpriseId: enterprise.enterprise_id,
										data: {
											email: inviteEmail,
											name: inviteName || undefined,
											role: inviteRole,
										},
									}).unwrap();
									showToast(
										'Success',
										'Invitation sent successfully',
										'success',
									);
									setShowInviteModal(false);
									setInviteEmail('');
									setInviteName('');
									setInviteRole('user');
									refetch();
								} catch (error: any) {
									const errorMessage =
										error?.data?.message ||
										error?.message ||
										'Failed to send invitation';
									showToast('Error', errorMessage, 'error');
								}
							}}
							variant="primary"
							loading={invitingUser}
							disabled={!inviteEmail}
						>
							Send Invitation
						</Button>
					</ModalFooter>
				</Modal>
			</PageContent>
		</div>
	);
};

export default EnterpriseDetailPage;
